import React from 'react';
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import PropTypes from 'prop-types';

import { GetJson, PostJson, DeleteJson, PutJson } from '@awp/helpers/fetchReact';
import { REST_API, STATE_STATUS } from '@awp/constants';
import { isDeferOnlyPreset } from '@awp/components/Graphs/MultiStation/helpers';
import { getWeatherStationById } from './weatherStationSlice';
import { saveTemplate } from './placementTemplatePacketSlice';

const initialState = {
  graphPresetId: null,
  graphPresets: [],
  originalPresetData: [],
  status: {
    get: STATE_STATUS.IDLE,
    save: STATE_STATUS.IDLE,
    delete: STATE_STATUS.IDLE,
    update: STATE_STATUS.IDLE,
    error: '',
  },
  newStationId: -100,
  newTypeId: -100,
};

export const currentGraphPresetId = (state) => state.graphPreset.graphPresetId;
export const selectAllGraphPresets = (state) => state.graphPreset.graphPresets;
export const selectAllOriginalPresets = (state) => state.graphPreset.originalPresetData;
// Thunk states
export const selectStatus = (state) => state.graphViewPage.status;
export const selectPresetGetState = (state) => state.graphPreset.status.get;
export const selectPresetSaveState = (state) => state.graphPreset.status.save;
export const selectPresetUpdateState = (state) => state.graphPreset.status.update;
export const selectPresetDeleteState = (state) => state.graphPreset.status.delete;

// Graph Presets
export const currentGraphPreset = (state) =>
  selectAllGraphPresets(state).find((preset) => preset.GraphPresetId == currentGraphPresetId(state));

export const originalGraphPreset = (state, presetId) => {
  const originalPresets = selectAllOriginalPresets(state);
  const foundPreset = originalPresets.find((preset) => preset.GraphPresetId === presetId);
  return foundPreset;
};

export const getPresetsForDropdown = (state) => {
  const allPresets = selectAllGraphPresets(state);

  return allPresets.map((p) => {
    return { value: p.GraphPresetId, label: p.Name, isPublic: p.IsPublic };
  });
};
//provide a psudo All dataset preset and other defer only presets for datatable or manual validation
export const getDeferOnlyPresetsForDropdown = (state) => {
  const allPresets = selectAllGraphPresets(state);
  let deferPresets = [];
  //Add the default psudo preset for datatable
  deferPresets.push({
    value: -999,
    label: 'All datasets',
    isPublic: true,
  });
  allPresets.map((p) => {
    if (isDeferOnlyPreset(p)) deferPresets.push({ value: p.GraphPresetId, label: p.Name, isPublic: p.IsPublic });
  });
  return deferPresets;
};
// export const getGraphPresetsForDropdown = createSelector(
//   [
//     (state) => state.graphPreset.graphPresets,
//   ],
//   (selectAllGraphPresets) => {

//   }
// )

export const getGraphPresetById = createSelector(
  [
    (state) => state.graphPreset.graphPresets,
    (_, GraphPresetId) => GraphPresetId,
    (state) => state, // Pass the entire state as an argument
  ],
  (selectAllGraphPresets, GraphPresetId, state) => {
    const graphPreset = selectAllGraphPresets.find((presets) => presets.GraphPresetId === GraphPresetId);

    // Now you can access and map weather station information for each PersistentGraphFilter within the graphPreset
    if (graphPreset && graphPreset.DataGraphs) {
      const dataGraphsWithWeather = graphPreset.DataGraphs.map((dataGraph) => {
        const persistentGraphFiltersWithWeather = dataGraph.PersistentGraphFilters.map((persistentGraphFilter) => {
          if (persistentGraphFilter.WeatherStationId) {
            const weatherStationInfo = getWeatherStationById(state, persistentGraphFilter.WeatherStationId);
            const filterStationInfo = weatherStationInfo
              ? {
                  Id: weatherStationInfo.Id,
                  Number: weatherStationInfo.Number,
                  Name: weatherStationInfo.Name,
                  DropdownText: weatherStationInfo.DropdownText,
                }
              : null;

            return {
              ...persistentGraphFilter,
              weatherStationInfo: filterStationInfo,
            };
          }
          return persistentGraphFilter;
        });

        return {
          ...dataGraph,
          PersistentGraphFilters: persistentGraphFiltersWithWeather,
        };
      });

      // Return the modified graphPreset with weather station information for each PersistentGraphFilter
      return {
        ...graphPreset,
        DataGraphs: dataGraphsWithWeather,
      };
    }

    return graphPreset;
  },
);

/**
 * Thunks
 */
export const fetchGraphPresets = createAsyncThunk('graphPreset/fetchGraphPresets', async () => {
  let url = REST_API.GRAPH_PRESETS;
  const response = await GetJson(url);
  return await response.json();
});

export const fetchSinglePreset = createAsyncThunk('graphPreset/fetchSinglePreset', async () => {
  let url = REST_API.GRAPH_PRESETS;
  const response = await GetJson(url);
  return await response.json();
});

export const graphPresetSlice = createSlice({
  name: 'graphPreset',
  initialState,
  reducers: {
    // Move a data graph within a preset
    moveDataGraph(state, action) {
      const { graphPresetId, displayOrder, direction } = action.payload;
      const presetIdx = state.graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);
      const currentPreset = state.graphPresets[presetIdx];

      // Create a copy of the data graphs for modification
      const dataGraphs = Object.assign([], currentPreset.DataGraphs);
      const curIndex = dataGraphs.findIndex((d) => d.DisplayOrder === displayOrder);

      if (direction === 'up') {
        // Swap the current data graph with the one above it
        [dataGraphs[curIndex], dataGraphs[curIndex - 1]] = [dataGraphs[curIndex - 1], dataGraphs[curIndex]];
      } else if (direction === 'down') {
        // Swap the current data graph with the one below it
        [dataGraphs[curIndex], dataGraphs[curIndex + 1]] = [dataGraphs[curIndex + 1], dataGraphs[curIndex]];
      }

      // Update the display order of data graphs
      const reorderedDataGraphs = dataGraphs.map((d, i) => {
        return { ...d, DisplayOrder: i + 1 };
      });

      // Update the state with the modified preset
      state.graphPresets[presetIdx] = { ...currentPreset, DataGraphs: reorderedDataGraphs };
    },
    // Remove a data graph from a preset
    removeDataGraph(state, action) {
      const { graphPresetId, displayOrder } = action.payload;
      const presetIdx = state.graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);
      const currentPreset = state.graphPresets[presetIdx];

      // Create a copy of the data graphs for modification
      const dataGraphs = Object.assign([], currentPreset.DataGraphs);

      // Find the index of the data graph to be removed and splice it out
      dataGraphs.splice(
        dataGraphs.findIndex((d) => d.DisplayOrder === displayOrder),
        1,
      );

      // Update the display order of remaining data graphs
      const reorderedDataGraphs = dataGraphs.map((d, i) => {
        return { ...d, DisplayOrder: i + 1 };
      });

      // Update the state with the modified preset
      state.graphPresets[presetIdx] = { ...currentPreset, DataGraphs: reorderedDataGraphs };
    },
    // Add a new data graph to a preset in the application state
    addNewDataGraph(state, action) {
      // Extract the graphPresetId from the action payload
      const { graphPresetId } = action.payload;

      // Find the index of the preset in the list of graph presets
      const presetIdx = state.graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);

      // Retrieve the current preset and the new station and type IDs from the state
      const currentPreset = state.graphPresets[presetIdx];
      const wxId = state.newStationId;
      const otId = state.newTypeId;

      // Create a copy of the data graphs for modification
      const dataGraphs = Object.assign([], currentPreset.DataGraphs);

      // Generate a new data graph and a new graph filter
      const newDataGraph = generateNewDataGraph(currentPreset.GraphPresetId, dataGraphs.length + 1);
      const newGraphFilter = generateNewPersistentGraphFilter(null);

      // Set the DataGraphId for the new graph filter
      newGraphFilter.DataGraphId = newDataGraph.DataGraphId;

      // Set the WeatherStationId and ObservationTypeId for the new graph filter
      newGraphFilter.WeatherStationId = wxId;
      newGraphFilter.ObservationTypeId = otId;

      // Update station and type IDs for the next use
      state.newStationId = wxId - 1;
      state.newTypeId = otId - 1;

      // Add the new graph filter to the new data graph
      newDataGraph.PersistentGraphFilters.push(newGraphFilter);

      // Add the new data graph to the preset's data graphs
      dataGraphs.push(newDataGraph);

      // Update the state with the modified preset, preserving the existing data graphs
      state.graphPresets[presetIdx] = { ...currentPreset, DataGraphs: dataGraphs };
    },
    // Update the name of a data graph
    updateGraphName(state, action) {
      const { graphPresetId, dataGraphIdx, newGraphName } = action.payload;
      const presetIdx = state.graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);
      const currentPreset = state.graphPresets[presetIdx];

      // Create a copy of the data graphs for modification
      const dataGraphs = Object.assign([], currentPreset.DataGraphs);

      // Update the name of the specified data graph
      dataGraphs[dataGraphIdx].Name = newGraphName;

      // Update the state with the modified preset
      state.graphPresets[presetIdx] = { ...currentPreset, DataGraphs: dataGraphs };
    },
    // Create a new preset in the application state
    createPreset(state, action) {
      // Extract the userSID from the action payload
      const { userSID } = action.payload;

      // Retrieve the new station and type IDs from the state
      const wxId = state.newStationId;
      const otId = state.newTypeId;

      // Generate a new preset with default values
      const newPreset = { ...generateNewPreset(userSID, -1) };

      // Generate a new graph filter and set weather and type IDs
      const newGraphFilter = generateNewPersistentGraphFilter(null);
      newGraphFilter.WeatherStationId = wxId;
      newGraphFilter.ObservationTypeId = otId;

      // Update station and type IDs for the next use
      state.newStationId = wxId - 1;
      state.newTypeId = otId - 1;

      // Add the new graph filter to the new data graph
      newPreset.DataGraphs[0].PersistentGraphFilters.push(newGraphFilter);

      // Add the new preset to both the list of graph presets and original preset data
      state.graphPresets.push(newPreset);
      state.originalPresetData.push(newPreset);
    },
    // Update a preset in the application state
    saveGraphPreset(state, action) {
      // Extract the preset data and original preset ID from the action payload
      const { presetData, originalPresetId } = action.payload;

      // Get references to the current graph presets and original preset data
      const graphPresets = state.graphPresets;
      const originalPresets = state.originalPresetData;

      // Determine the ID of the preset to find, either the new or original ID
      const presetToFindId =
        originalPresetId === presetData.GraphPresetId ? presetData.GraphPresetId : originalPresetId;

      // Remove the preset from the list of graph presets
      const presetIdx = graphPresets.findIndex((preset) => preset.GraphPresetId === presetToFindId);
      graphPresets.splice(presetIdx, 1);

      // Remove the original copy of the preset from the list of original presets
      const originalIdx = originalPresets.findIndex((preset) => preset.GraphPresetId === presetToFindId);
      originalPresets.splice(originalIdx, 1);

      //if the id changed (create) we need to set which one we are on
      state.graphPresetId = presetData.GraphPresetId;
      // Append the new preset data to both the list of graph presets and original presets
      state.graphPresets.push(presetData);
      state.originalPresetData.push(presetData);
    },
    // Delete a preset from the application state
    deletePreset(state, action) {
      // Extract the graphPresetId from the action payload
      const { graphPresetId } = action.payload;

      // Get references to the current graph presets and original preset data
      const graphPresets = state.graphPresets;
      const originalPresets = state.originalPresetData;

      // Find the index of the preset to be deleted in the list of graph presets
      const presetIdx = graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);

      // Remove the preset from the list of graph presets
      graphPresets.splice(presetIdx, 1);

      // Find the index of the same preset in the list of original presets
      const originalIdx = originalPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);

      // Remove the same preset from the list of original presets
      originalPresets.splice(originalIdx, 1);

      // Update the application state with the modified lists of presets
      state.graphPresets = graphPresets;
      state.originalPresetData = originalPresets;
    },
    // Set the name of a preset
    setPresetName(state, action) {
      const { graphPresetId, newPresetName } = action.payload;

      const presetIdx = state.graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);
      const currentPreset = state.graphPresets[presetIdx];

      // Update the name of the specified preset
      state.graphPresets[presetIdx] = { ...currentPreset, Name: newPresetName };
    },
    // Reset a preset to its original state in the application state
    resetPreset(state, action) {
      // Extract the graphPresetId from the action payload
      const { graphPresetId } = action.payload;

      // Get references to the current graph presets and the original preset data
      const graphPresets = state.graphPresets;

      // Find the index of the preset to be reset in the list of graph presets
      const presetIdx = graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);

      // Find the original preset with the same ID in the list of original presets
      const originalPreset = state.originalPresetData.find((preset) => preset.GraphPresetId === graphPresetId);

      // Restore the preset to its original state by replacing it with the original preset
      state.graphPresets[presetIdx] = originalPreset;
    },
    // Add a new graph filter to a data graph
    addNewGraphFilter(state, action) {
      const { graphPresetId, displayOrder } = action.payload;
      const wxId = state.newStationId;
      const otId = state.newTypeId;

      const presetIdx = state.graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);
      const currentPreset = state.graphPresets[presetIdx];

      // Find the data graph specified by display order
      const currentDataGraph = currentPreset.DataGraphs.find((dg) => dg.DisplayOrder === displayOrder);

      // Generate a new graph filter and set data, weather, and type IDs
      const newGraphFilter = generateNewPersistentGraphFilter(null);
      newGraphFilter.DataGraphId = currentDataGraph.DataGraphId;
      newGraphFilter.WeatherStationId = wxId;
      newGraphFilter.ObservationTypeId = otId;

      // Update station and type IDs for the next use
      state.newStationId = wxId - 1;
      state.newTypeId = otId - 1;

      // Add the new graph filter to the specified data graph
      currentDataGraph.PersistentGraphFilters.push(newGraphFilter);

      // Update the state with the modified preset
      state.graphPresets[presetIdx] = { ...currentPreset };
    },
    // Remove a graph filter from a data graph
    removeGraphFilter(state, action) {
      const { graphPresetId, weatherStationId, displayOrder } = action.payload;

      // Payload passes the values as strings, convert string to a number if it's not "null"
      const actualWeatherStationId = weatherStationId === 'null' ? null : parseInt(weatherStationId);

      const presetIdx = state.graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);
      const currentPreset = state.graphPresets[presetIdx];

      // Find the data graph specified by display order
      const currentDataGraph = { ...currentPreset.DataGraphs.find((dg) => dg.DisplayOrder === displayOrder) };

      // Filter the PersistentGraphFilters array to remove items with the specified WeatherStationId
      currentDataGraph.PersistentGraphFilters = currentDataGraph.PersistentGraphFilters.filter(
        (pgf) => pgf.WeatherStationId !== actualWeatherStationId,
      );

      //if we deleted all the filters then we'll add back a blank one
      if (currentDataGraph.PersistentGraphFilters.length === 0) {
        const wxId = state.newStationId;
        const otId = state.newTypeId;

        const newGraphFilter = generateNewPersistentGraphFilter(null);
        newGraphFilter.DataGraphId = currentDataGraph.DataGraphId;
        newGraphFilter.WeatherStationId = wxId;
        newGraphFilter.ObservationTypeId = otId;

        // Update station and type IDs for the next use
        state.newStationId = wxId - 1;
        state.newTypeId = otId - 1;

        // Add the new graph filter
        currentDataGraph.PersistentGraphFilters.push(newGraphFilter);
      }
      // Update the data graph within the copied preset
      currentPreset.DataGraphs = currentPreset.DataGraphs.map((dg) =>
        dg.DisplayOrder === displayOrder ? currentDataGraph : dg,
      );

      // Update the state with the modified preset
      state.graphPresets[presetIdx] = { ...currentPreset };
    },
    addDataset(state, action) {
      const { graphPresetId, weatherStationId, displayOrder } = action.payload;
      const otId = state.newTypeId;

      // Payload passes the values as strings, convert string to a number if it's not "null"
      const actualWeatherStationId = weatherStationId === 'null' ? null : parseInt(weatherStationId);

      const presetIdx = state.graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);
      const currentPreset = state.graphPresets[presetIdx];
      const currentDataGraph = { ...currentPreset.DataGraphs.find((dg) => dg.DisplayOrder === displayOrder) };

      // add a default/blank graph filter
      const newGraphFilter = generateNewPersistentGraphFilter(null);
      newGraphFilter.DataGraphId = currentDataGraph.DataGraphId;
      newGraphFilter.WeatherStationId = actualWeatherStationId;
      newGraphFilter.ObservationTypeId = otId;

      // Update the type ID for the next use
      state.newTypeId = otId - 1;

      // Add the new graph filter
      currentDataGraph.PersistentGraphFilters.push(newGraphFilter);

      // Update the data graph within the copied preset
      currentPreset.DataGraphs = currentPreset.DataGraphs.map((dg) =>
        dg.DisplayOrder === displayOrder ? currentDataGraph : dg,
      );

      // Update the state with the modified preset
      state.graphPresets[presetIdx] = { ...currentPreset };
    },
    // Remove a dataset from a data graph
    removeDataset(state, action) {
      const { graphPresetId, weatherStationId, displayOrder, placementName, observationTypeId } = action.payload;
      const otId = state.newTypeId;

      // Payload passes the values as strings, convert string to a number if it's not "null"
      const actualWeatherStationId = weatherStationId === 'null' ? null : parseInt(weatherStationId);

      const presetIdx = state.graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);
      const currentPreset = state.graphPresets[presetIdx];
      const currentDataGraph = { ...currentPreset.DataGraphs.find((dg) => dg.DisplayOrder === displayOrder) };

      // Find the index of the graph filter to be removed
      // const pgfIndex = currentDataGraph.PersistentGraphFilters.findIndex(
      //   (pgf) => pgf.ObservationTypeId === observationTypeId && pgf.WeatherStationId === actualWeatherStationId,
      // );
      const pgfIndex = currentDataGraph.PersistentGraphFilters.findIndex(
        (pgf) =>
          pgf.ObservationTypeId === observationTypeId &&
          pgf.WeatherStationId === actualWeatherStationId &&
          (placementName === null || pgf.PlacementName === placementName),
      );

      // Remove the graph filter
      currentDataGraph.PersistentGraphFilters.splice(pgfIndex, 1);

      // Check if we removed the last graph filter for this weather station
      if (
        currentDataGraph.PersistentGraphFilters.filter((pgf) => pgf.WeatherStationId === actualWeatherStationId)
          .length === 0
      ) {
        // If so, add back a default/blank graph filter
        const newGraphFilter = generateNewPersistentGraphFilter(null);
        newGraphFilter.DataGraphId = currentDataGraph.DataGraphId;
        newGraphFilter.WeatherStationId = actualWeatherStationId;
        newGraphFilter.ObservationTypeId = otId;

        // Update the type ID for the next use
        state.newTypeId = otId - 1;

        // Add the new graph filter
        currentDataGraph.PersistentGraphFilters.push(newGraphFilter);
      }

      // Update the data graph within the copied preset
      currentPreset.DataGraphs = currentPreset.DataGraphs.map((dg) =>
        dg.DisplayOrder === displayOrder ? currentDataGraph : dg,
      );

      // Update the state with the modified preset
      state.graphPresets[presetIdx] = { ...currentPreset };
    },
    // Update a graph filter
    updateGraphFilterTypeId(state, action) {
      const {
        graphPresetId,
        weatherStationId,
        displayOrder,
        placementName,
        prevPlacementName,
        currentTypeId,
        newTypeId,
        obsObject,
      } = action.payload;
      const actualWeatherStationId = weatherStationId === 'null' ? null : parseInt(weatherStationId);

      const presetIdx = state.graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);
      const currentPreset = state.graphPresets[presetIdx];
      const currentDataGraph = { ...currentPreset.DataGraphs.find((dg) => dg.DisplayOrder === displayOrder) };

      // Find the index of the graph filter to be adjusted
      const pgfIndex = currentDataGraph.PersistentGraphFilters.findIndex(
        (pgf) =>
          pgf.ObservationTypeId === currentTypeId &&
          pgf.WeatherStationId === actualWeatherStationId &&
          (prevPlacementName !== null && currentTypeId > 0 ? pgf.PlacementName === prevPlacementName : true),
      );

      if (obsObject.DatasetPrefix === '1') {
        currentDataGraph.PersistentGraphFilters[pgfIndex].PlacementName = obsObject.ObservationTypeLabel;
        currentDataGraph.PersistentGraphFilters[pgfIndex].DatasetPrefix = obsObject.DatasetPrefix;
      } else {
        currentDataGraph.PersistentGraphFilters[pgfIndex].PlacementName = placementName;
      }

      currentDataGraph.PersistentGraphFilters[pgfIndex].ObservationTypeId = newTypeId;

      // Update the data graph within the copied preset
      currentPreset.DataGraphs = currentPreset.DataGraphs.map((dg) =>
        dg.DisplayOrder === displayOrder ? currentDataGraph : dg,
      );

      // Update the state with the modified preset
      state.graphPresets[presetIdx] = { ...currentPreset };
    },
    // Update a graph filter
    updateGraphFilterColorOverride(state, action) {
      const { graphPresetId, weatherStationId, displayOrder, typeId, datasetPrefix, placementName, colorValue } =
        action.payload;
      const actualWeatherStationId = weatherStationId === 'null' ? null : parseInt(weatherStationId);

      const presetIdx = state.graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);
      const currentPreset = state.graphPresets[presetIdx];
      const currentDataGraph = { ...currentPreset.DataGraphs.find((dg) => dg.DisplayOrder === displayOrder) };

      // Find the index of the graph filter to be adjusted, the find is different based on datasetPrefix
      let pgfIndex;
      if (datasetPrefix == '1') {
        pgfIndex = currentDataGraph.PersistentGraphFilters.findIndex(
          (pgf) => pgf.PlacementName === placementName && pgf.WeatherStationId === actualWeatherStationId,
        );
      } else {
        pgfIndex = currentDataGraph.PersistentGraphFilters.findIndex(
          (pgf) =>
            pgf.ObservationTypeId === typeId &&
            pgf.WeatherStationId === actualWeatherStationId &&
            (!placementName || pgf.PlacementName === placementName),
        );
      }

      currentDataGraph.PersistentGraphFilters[pgfIndex].ColorCodeOverride = colorValue;

      // Update the data graph within the copied preset
      currentPreset.DataGraphs = currentPreset.DataGraphs.map((dg) =>
        dg.DisplayOrder === displayOrder ? currentDataGraph : dg,
      );

      // Update the state with the modified preset
      state.graphPresets[presetIdx] = { ...currentPreset };
    },
    // Update a graph filter
    updateGraphFilterWeatherStation(state, action) {
      const { graphPresetId, displayOrder, weatherStationId, newWeatherStationId } = action.payload;
      const actualWeatherStationId = weatherStationId === 'null' ? null : parseInt(weatherStationId);
      const actualNewWeatherStationId = newWeatherStationId === 'null' ? null : parseInt(newWeatherStationId);

      const presetIdx = state.graphPresets.findIndex((preset) => preset.GraphPresetId === graphPresetId);
      const currentPreset = state.graphPresets[presetIdx];
      const currentDataGraph = { ...currentPreset.DataGraphs.find((dg) => dg.DisplayOrder === displayOrder) };

      // Find the index of the graph filter to be changed
      //  there is only one when weatherStation is being changed
      const pgfIndex = currentDataGraph.PersistentGraphFilters.findIndex(
        (pgf) => pgf.WeatherStationId === actualWeatherStationId,
      );

      currentDataGraph.PersistentGraphFilters[pgfIndex].WeatherStationId = actualNewWeatherStationId;

      // Update the data graph within the copied preset
      currentPreset.DataGraphs = currentPreset.DataGraphs.map((dg) =>
        dg.DisplayOrder === displayOrder ? currentDataGraph : dg,
      );

      // Update the state with the modified preset
      state.graphPresets[presetIdx] = { ...currentPreset };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGraphPresets.pending, (state) => {
        state.status.get = STATE_STATUS.LOADING;
      })
      .addCase(fetchGraphPresets.fulfilled, (state, action) => {
        state.originalPresetData = action.payload;
        state.graphPresets = action.payload;
        state.status.get = STATE_STATUS.SUCCESS;
      })
      .addCase(fetchGraphPresets.rejected, (state, action) => {
        state.error = action.error.message;
        state.status.get = STATE_STATUS.FAILED;
      })
      .addCase(fetchSinglePreset.pending, (state) => {
        state.state.get = STATE_STATUS.LOADING;
      })
      .addCase(fetchSinglePreset.fulfilled, (state, action) => {
        /*state.originalPresetData = action.payload;
        state.graphPresets = action.payload;*/
        state.status.get = STATE_STATUS.SUCCESS;
      })
      .addCase(fetchSinglePreset.rejected, (state, action) => {
        state.error = action.error.message;
        state.status.get = STATE_STATUS.FAILED;
      });
  },
});

export const {
  moveDataGraph,
  removeDataGraph,
  addNewDataGraph,
  updateGraphName,
  createPreset,
  resetPreset,
  deletePreset,
  setPresetName,
  addNewGraphFilter,
  removeGraphFilter,
  addDataset,
  removeDataset,
  updateGraphFilterTypeId,
  updateGraphFilterColorOverride,
  updateGraphFilterWeatherStation,
  saveGraphPreset,
} = graphPresetSlice.actions;

/**
 * EXTRA FUNCTIONS
 */
export const generateNewPreset = (userSID, presetId) => {
  const newPreset = {
    GraphPresetId: presetId,
    Name: null,
    SecurityIdentifierId: userSID,
    Concurrency: 0,
    IsPublic: false,
    DataGraphs: [generateNewDataGraph(presetId, 1)],
  };
  return newPreset;
};

generateNewPreset.propTypes = {
  userSID: PropTypes.number.isRequired,
};

export const generateNewDataGraph = (graphPresetId, displayOrder) => {
  const newDataGraph = {
    DisplayOrder: displayOrder,
    Name: null,
    Description: null,
    DataGraphViewId: null,
    DataGraphPresetId: graphPresetId,
    PersistentGraphFilters: [],
  };
  return newDataGraph;
};

generateNewDataGraph.propTypes = {
  graphPresetId: PropTypes.number.isRequired,
  displayOrder: PropTypes.number.isRequired,
};

export const generateNewPersistentGraphFilter = (dataGraphId) => {
  const newPersistentGraphFilter = {
    DisplayOrder: null,
    DisplayName: null,
    PlacementName: null,
    ColorCodeOverride: null,
    DataGraphId: dataGraphId,
    WeatherStationId: null,
    ObservationTypeId: null,
    DatasetPrefix: null,
  };

  return newPersistentGraphFilter;
};

generateNewPersistentGraphFilter.PropTypes = {
  dataGraphId: PropTypes.number.isRequired,
  weatherStationId: PropTypes.number,
};
